import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { subdomain } from "shared/stores/oidc";
import { fail } from "shared/utils/fns";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { FeatureType } from "src/gql/graphql";
import { FeatureTypeSchema } from "src/gql/zod";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

const query = queryOptions({
    queryKey: qk("customer", "features", subdomain),
    queryFn: () =>
        httpPostGraphql(
            graphql(`
                query Features($subdomain: String!) {
                    customer(subDomain: $subdomain) {
                        features
                    }
                }
            `),
            { subdomain },
        ),
});

type FeatureTypeLike = FeatureType | `${FeatureType}`;

type Features = {
    enabled(check: FeatureTypeLike): boolean;
    enabled(checks: FeatureTypeLike[], opts?: { strategy?: "any" | "all" }): boolean;
    load: () => Promise<void>;
    refresh: () => Promise<void>;
    useLoader: () => void;
    useGuard(check: FeatureTypeLike): void;
    useGuard(check: FeatureTypeLike[], opts?: { strategy?: "any" | "all" }): void;
};

export const features: Features = {
    enabled(checks: FeatureTypeLike | FeatureTypeLike[], opts?: { strategy?: "any" | "all" }): boolean {
        const features = queryClient.getQueryData(query.queryKey)?.customer.features ?? fail("Features not loaded");
        return match(opts?.strategy ?? "all")
            .with("all", () => [checks].flat().every((check) => features.includes(FeatureTypeSchema.parse(check))))
            .with("any", () => [checks].flat().some((check) => features.includes(FeatureTypeSchema.parse(check))))
            .exhaustive();
    },
    useGuard(check: FeatureTypeLike | FeatureTypeLike[], opts?: { strategy?: "any" | "all" }) {
        const nav = useNavigate();

        const passed = match(opts?.strategy ?? "all")
            .with("all", () => [check].flat().every((check) => features.enabled(check)))
            .with("any", () => [check].flat().some((check) => features.enabled(check)))
            .exhaustive();

        if (!passed) void nav({ to: "/" });
    },
    async load() {
        await queryClient.ensureQueryData(query);
    },
    useLoader() {
        useSuspenseQuery(query);
    },
    refresh() {
        return qk.invalidate("customer", "features");
    },
};
